import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Per-user scroll position for a single book. Reads the existing row
// once on mount; writes are upserted with last_read_at touched on
// every save so the dashboard "continue reading" surface can sort by
// recency.
//
// `saveProgress(percent)` is debounce-friendly — the reader page
// throttles scroll events itself and calls this every few seconds at
// most.
export function useReadingProgress(bookId) {
  const { user } = useAuth()
  const userId = user?.id || null
  const [position, setPosition] = useState(null)
  const [lastReadAt, setLastReadAt] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const lastSavedRef = useRef(0)
  const lastSavedAtRef = useRef(0)

  useEffect(() => {
    if (!userId || !bookId) { setLoaded(true); return undefined }
    let alive = true
    ;(async () => {
      const { data } = await supabase
        .from('reading_progress')
        .select('position_percent, last_read_at')
        .eq('book_id', bookId)
        .eq('user_id', userId)
        .maybeSingle()
      if (alive) {
        const pct = Number(data?.position_percent ?? 0)
        setPosition(Number.isFinite(pct) ? pct : 0)
        setLastReadAt(data?.last_read_at || null)
        lastSavedRef.current = Number.isFinite(pct) ? pct : 0
        setLoaded(true)
      }
    })()
    return () => { alive = false }
  }, [userId, bookId])

  const saveProgress = useCallback(async (percent) => {
    if (!userId || !bookId) return
    const clamped = Math.max(0, Math.min(100, Number(percent) || 0))
    // Skip if the delta is trivial AND we saved very recently — avoids
    // bombarding the table with near-identical rows during fast scrolls.
    const now = Date.now()
    if (Math.abs(clamped - lastSavedRef.current) < 0.5 && now - lastSavedAtRef.current < 8000) return
    lastSavedRef.current = clamped
    lastSavedAtRef.current = now
    await supabase
      .from('reading_progress')
      .upsert({
        book_id: bookId,
        user_id: userId,
        position_percent: clamped,
        last_read_at: new Date().toISOString(),
      }, { onConflict: 'book_id,user_id' })
  }, [userId, bookId])

  return { position, lastReadAt, loaded, saveProgress }
}
